/**
 * 会計管理リポジトリ
 */
import { adminApiFetch } from '../utils/adminApi.js';
import { buildApiUrl } from '../utils/apiBase.js';
import { notifyUnauthorized } from '../utils/authController.js';

class AdminAccountingRepository {
  static instance = null;

  static getInstance() {
    if (!AdminAccountingRepository.instance) {
      AdminAccountingRepository.instance = new AdminAccountingRepository();
    }
    return AdminAccountingRepository.instance;
  }

  async getStatus() {
    return adminApiFetch('/api/admin/accounting/status');
  }

  async listRuns(limit = 24) {
    const data = await adminApiFetch(`/api/admin/accounting/runs?limit=${encodeURIComponent(limit)}`);
    return data.runs || [];
  }

  async getRun(runId) {
    const data = await adminApiFetch(`/api/admin/accounting/runs/${encodeURIComponent(runId)}`);
    return data.run;
  }

  async getLatestRun(yearMonth) {
    const data = await adminApiFetch(
      `/api/admin/accounting/months/${encodeURIComponent(yearMonth)}/latest`,
    );
    return data.run || null;
  }

  async getPreview(runId, fileKey, { page = 1, pageSize = 100 } = {}) {
    const query = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
    });
    const data = await adminApiFetch(
      `/api/admin/accounting/runs/${encodeURIComponent(runId)}`
      + `/preview/${encodeURIComponent(fileKey)}?${query.toString()}`,
    );
    return data.preview;
  }

  async generate({ yearMonth, uploadToDrive = false } = {}) {
    return adminApiFetch('/api/admin/accounting/generate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ yearMonth, uploadToDrive }),
    });
  }

  async migrateTax({ recreatePrice = true, confirmLive = false, productId = null } = {}) {
    return adminApiFetch('/api/admin/accounting/migrate-tax', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        recreatePrice,
        confirmLive,
        productId: productId || null,
      }),
    });
  }

  async dryRunSubscriptionTaxMigration({ productId = null, limit = 0 } = {}) {
    return adminApiFetch('/api/admin/accounting/migrate-subscription-tax/dry-run', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId: productId || null, limit: Number(limit) || 0 }),
    });
  }

  async migrateSubscriptionTax({
    dryRun = true,
    confirmLive = false,
    productId = null,
    limit = 0,
    classes = null,
  } = {}) {
    return adminApiFetch('/api/admin/accounting/migrate-subscription-tax', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dryRun,
        confirmLive,
        productId: productId || null,
        limit: Number(limit) || 0,
        classes,
      }),
    });
  }

  async uploadDrive(runId) {
    return adminApiFetch(`/api/admin/accounting/runs/${encodeURIComponent(runId)}/upload-drive`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
  }

  async downloadResponse(path, fallbackName) {
    const response = await fetch(
      buildApiUrl(path),
      { credentials: 'include' },
    );
    if (!response.ok) {
      if (response.status === 401) notifyUnauthorized();
      const data = await response.json().catch(() => ({}));
      throw new Error(data.message || `ダウンロード失敗 (${response.status})`);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('content-disposition') || '';
    const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
    const fileName = encodedName
      ? decodeURIComponent(encodedName)
      : fallbackName;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async downloadFile(runId, fileKey, fileName) {
    return this.downloadResponse(
      `/api/admin/accounting/runs/${encodeURIComponent(runId)}`
      + `/files/${encodeURIComponent(fileKey)}`,
      fileName || fileKey,
    );
  }

  async downloadAll(runId, yearMonth) {
    return this.downloadResponse(
      `/api/admin/accounting/runs/${encodeURIComponent(runId)}/download-all`,
      `stripe-accounting_${yearMonth}.zip`,
    );
  }
}

export default AdminAccountingRepository;
